import AvatarMini from "components/commons/AvatarMini"
import { Dialog } from "primereact/dialog"
import { Knob } from "primereact/knob"
import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { selectCompanyGuid } from "features/auth/authSlice"
import { useWebSocket } from "app/ws/WebSocketProvider"
import { useDeleteDeviceMutation, useSetDeviceStatusMutation } from "../api/devicesStatusApiSlice"
import { relativeTime } from "utility/TimeUtility"


export default function DeviceCard({
    device,
    canManageDevices = false,
    highlighted = false,
}) {
    const MAX_DATA_AGE_SECONDS = 60 * 60 * 24;
    const [t] = useTranslation(["global", "device"])
    const navigate = useNavigate()
    const rowRef = useRef(null)
    const companyGuid = useSelector(selectCompanyGuid)
    const { client: wsClient } = useWebSocket()
    const [setStatusRequest] = useSetDeviceStatusMutation()

    useEffect(() => {
        if (highlighted && rowRef.current) {
            rowRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
        }
    }, [highlighted])

    const handleAction = (actionType) => {
        try {
            wsClient.publish(
                `company/${companyGuid}/device/${device.guid}/request`,
                JSON.stringify({ type: actionType })
            )
        } catch (err) {
            console.error(`Error trying to publish mqtt command`)
        }
    }

    const handleChangeStatus = (e) => {
        setStatusRequest({ deviceGuid: device.guid, enabled: e.target.checked })
    }

    function DeviceStatusBadge() {
        let color
        let textColor = "white"
        switch (device.smartStatus) {
            case "CHARGING": color = "bg-primary"; break
            case "OFFBODY":  color = "bg-warning"; textColor = "dark"; break
            case "IN_USE":   color = "bg-success"; break
            case "DISABLED": color = "bg-danger";  break
            default:         color = "dark"
        }
        return (
            <span className={`badge rounded-pill ${color} text-${textColor}`} style={{ opacity: calculateAlpha() }}>
                {device.status.isCharging && <i className="fa-solid fa-bolt" />}{" "}
                {t(`device:entity.device.smart_status.${device.smartStatus}`).toUpperCase()}
            </span>
        )
    }

    function getDeviceTypeIcon() {
        switch (device.type) {
            case "WATCH":  return <i className="fa-solid fa-clock" />
            case "DEVICE": return <i className="fa-solid fa-tablet-screen-button" />
            default:       return null
        }
    }

    const getBatteryColor = () => {
        if (device.status.isCharging)          return "#0d6efd"
        if (device.status.batteryLevel < 15)   return "#cd3342"
        if (device.status.batteryLevel < 25)   return "orange"
        if (device.status.batteryLevel < 50)   return "#ffc107"
        return "#198754"
    }

    function calculateAlpha(nowSeconds = Math.floor(Date.now() / 1000)) {
        const age = Math.max(0, nowSeconds - device.lastUpdate)
        return age >= MAX_DATA_AGE_SECONDS ? 0 : 1 - age / MAX_DATA_AGE_SECONDS
    }

    return (
        <tr ref={rowRef} style={highlighted ? { "--bs-table-bg": "rgba(21, 227, 175, 0.2)" } : {}}>
            <td className="text-muted">{device.guid.substr(device.guid.length - 5)}</td>
            <td>
                <div className="form-check form-switch">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        disabled={!canManageDevices}
                        checked={device.enabled}
                        onChange={handleChangeStatus}
                    />
                </div>
            </td>
            <td><small className="text-muted">{device.ip}</small></td>
            <td>
                <div className="d-flex align-items-center gap-2">
                    {getDeviceTypeIcon()}{device.model}
                </div>
                <small className="text-muted">{device.version}</small>
            </td>
            <td>
                {device.status?.batteryLevel != null &&
                    <Knob
                        value={device.status.batteryLevel}
                        valueTemplate={`${device.status.batteryLevel}`}
                        valueColor={getBatteryColor()}
                        strokeWidth={8}
                        size={72}
                        style={{ opacity: calculateAlpha() }}
                    />
                }
            </td>
            <td><DeviceStatusBadge /></td>
            <td><div className="text-muted">{relativeTime(device.lastUpdate)}</div></td>
            <td>
                {device.lastUser &&
                    <AvatarMini
                        displayName={device.lastUser.displayName}
                        guid={device.lastUser.guid}
                        abbreviate={true}
                    />
                }
            </td>
            <td>
                {device.status.isLogged === true ? (
                    <div className="btn-group">
                        <button className="btn btn-primary" onClick={() => handleAction("UPDATE_STATUS")}>
                            <i className="fa-solid fa-retweet" /> Refresh
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary dropdown-toggle dropdown-toggle-split"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                            <span className="visually-hidden">Toggle Dropdown</span>
                        </button>
                        <ul className="dropdown-menu">
                            <li>
                                <button className="dropdown-item" onClick={() => handleAction("IDENTIFY")}>
                                    <i className="fa-solid fa-eye" /> Identifica
                                </button>
                            </li>
                            <li>
                                <button className="dropdown-item" onClick={() => handleAction("ASK_FOR_RETURN")}>
                                    <i className="fa-solid fa-house-flag" /> Chiedi il ritorno
                                </button>
                            </li>
                            <li><hr className="dropdown-divider" /></li>
                            <li>
                                <button className="dropdown-item" onClick={() =>
                                    navigate("/dashboard/devices/diagnostics/logs", { state: { deviceGuid: device.guid } })
                                }>
                                    <i className="fa-solid fa-bug me-1" />{t("device:diagnostics.title")}
                                </button>
                            </li>
                            {canManageDevices && <>
                                <li>
                                    <button className="dropdown-item" onClick={() => handleAction("LOGOUT")}>
                                        <i className="fa-solid fa-right-from-bracket" /> Disconnetti
                                    </button>
                                </li>
                                <li>
                                    <DeleteDeviceTemplate device={device} btnClassName="dropdown-item text-danger" />
                                </li>
                            </>}
                        </ul>
                    </div>
                ) : (
                    <div className="d-flex gap-1">
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => navigate("/dashboard/devices/diagnostics/logs", { state: { deviceGuid: device.guid } })}
                        >
                            <i className="fa-solid fa-bug" />
                        </button>
                        {canManageDevices &&
                            <DeleteDeviceTemplate device={device} btnClassName="btn btn-danger text-white" />
                        }
                    </div>
                )}
            </td>
        </tr>
    )
}


const DeleteDeviceTemplate = ({ device, btnClassName = "btn btn-danger" }) => {
    const [t] = useTranslation(["global", "device"])
    const [openDialog, setOpenDialog] = useState(false)
    const [deleteDevice] = useDeleteDeviceMutation()
    return (
        <>
            <button className={btnClassName} onClick={() => setOpenDialog(true)}>
                <i className="fa-solid fa-trash" /> {t("action.delete")}
            </button>
            <Dialog
                header={t("device:actions.delete_device")}
                visible={openDialog}
                onHide={() => setOpenDialog(false)}
            >
                <span>
                    {t("device:helper.device_delete_confirm")} <b>{device.guid.substr(device.guid.length - 5)}</b> ?
                </span>
                <div className="text-end mt-4">
                    <button className="btn btn-danger mx-2" onClick={() => {
                        deleteDevice(device.guid)
                        setOpenDialog(false)
                    }}>
                        {t("action.delete")}
                    </button>
                    <button className="btn btn-secondary" onClick={() => setOpenDialog(false)}>
                        {t("action.cancel")}
                    </button>
                </div>
            </Dialog>
        </>
    )
}
